import type { ConversationRole } from '@/services/agents/config';

/**
 * Whether the caller may write to a conversation.
 *
 * `unknown` is the state that matters: an existing conversation's role only
 * arrives with its detail load, so between mount and that response we do
 * not yet know whether this is the caller's own chat or one shared with
 * them view-only. Treating that window as writable paints a live composer
 * a viewer can type into, and the send only fails at the API.
 */
export type ConversationWriteAccess = 'allowed' | 'denied' | 'unknown';

export function conversationWriteAccess({
  conversationId,
  role,
  isLoading,
}: {
  conversationId: string | null;
  role: ConversationRole | null;
  isLoading: boolean;
}): ConversationWriteAccess {
  // Nothing to be a viewer of yet: an unthreaded composer starts the
  // caller's own conversation.
  if (!conversationId) return 'allowed';
  if (role === 'viewer') return 'denied';
  if (role === null && isLoading) return 'unknown';
  // Role resolved as owner, or the load finished without one (a missing or
  // unreadable conversation). The latter mints a fresh conversation on
  // send, so the composer belongs there.
  return 'allowed';
}

/** The single place the viewer predicate is spelled out. */
export function isConversationViewer(
  role: ConversationRole | null | undefined,
): boolean {
  return role === 'viewer';
}
